import { queryKey } from '../constants/queryClientKey';
import {
  createProcedure,
  deleteProcedure,
  deleteProcedureGroup,
  getProcedures,
  getProceduresGroup,
  ParamsCreateProcedure,
  ParamsCreateProceduresGroup,
  ParamsUpdateProcedure,
  ParamsUpdateProcedureGroup,
} from '../apis/procedure.api';
import {
  defaultGetNextPageParam,
  sortObjectKeys,
} from '@vindr/utils/helpers/common.helper';
import {
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  UseInfiniteQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
  UseMutationResult,
} from '@tanstack/react-query';
import {
  createProcedureGroup,
  updateProcedure,
  updateProcedureGroup,
} from './../apis/procedure.api';
import { ResponseGetProcedure } from '../apis/procedure.api';
import { ParamsGetProcedure } from '../apis/procedure.api';

export function useGetProcedures(params: Parameters<typeof getProcedures>[0]) {
  return useQuery(
    [queryKey.procedure, sortObjectKeys(params)],
    () => getProcedures(params),
    { keepPreviousData: true }
  );
}

export function useGetProcedure() {
  return useMutation(
    async (param: Parameters<typeof getProcedures>[0]) => getProcedures(param),
    {
      onError: (err) => {
        console.error('useUpdateProcedure:', err);
      },
    }
  ) as UseMutationResult<
    ResponseGetProcedure,
    unknown,
    ParamsGetProcedure,
    unknown
  >;
}

export function useInfinityGetProcedures(
  params: Parameters<typeof getProcedures>[0],
  options?: UseInfiniteQueryOptions
) {
  return useInfiniteQuery(
    [queryKey.procedure, sortObjectKeys(params)],
    (e) =>
      getProcedures({
        ...params,
        offset: (e.pageParam || 0) + (params?.offset || 0),
      }),
    {
      getNextPageParam: defaultGetNextPageParam,
      ...options,
    }
  ) as UseInfiniteQueryResult<Awaited<ReturnType<typeof getProcedures>>>;
}

export function useGetProceduresGroup(
  params: Parameters<typeof getProceduresGroup>[0]
) {
  return useQuery(
    [queryKey.procedure_group, sortObjectKeys(params)],
    () => getProceduresGroup(params),
    {}
  );
}

export function useCreateProcedure() {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: ParamsCreateProcedure) => createProcedure(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.procedure]);
      },
      onError: (err) => {
        console.error('useCreateProcedure:', err);
      },
    }
  );
}

export function useCreateProcedureGroup() {
  const queryClient = useQueryClient();

  return useMutation(
    async (params: ParamsCreateProceduresGroup) => createProcedureGroup(params),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.procedure_group]);
      },
      onError: (err) => {
        console.error('useCreateProcedureGroup:', err);
      },
    }
  );
}

export function useUpdateProcedure() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, data }: { id: string; data: ParamsUpdateProcedure }) =>
      updateProcedure(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.procedure]);
      },

      onError: (err) => {
        console.error('useUpdateProcedure:', err);
      },
    }
  );
}

export function useUpdateProcedureGroup() {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ id, data }: { id: string; data: ParamsUpdateProcedureGroup }) =>
      updateProcedureGroup(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey.procedure_group]);
      },

      onError: (err) => {
        console.error('useUpdateProcedureGroup:', err);
      },
    }
  );
}

export function useDeleteProcedure() {
  const queryClient = useQueryClient();

  return useMutation(async (id: string) => deleteProcedure(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.procedure]);
    },
    onError: (err) => {
      console.error('useDeleteProcedure:', err);
    },
  });
}

export function useDeleteProcedureGroup() {
  const queryClient = useQueryClient();

  return useMutation(async (id: string) => deleteProcedureGroup(id), {
    onSuccess: () => {
      queryClient.invalidateQueries([queryKey.procedure_group]);
    },
    onError: (err) => {
      console.error('useDeleteProcedureGroup', err);
    },
  });
}
